import { createAction } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';
import EcomEngineAPI from 'api/ecom_engine_api';
import { CheckoutDialogStatesEnum } from 'utils/constant';
import { phoneNumber, phoneNumberLink, trackError } from 'utils/helpers';
import { fetchCurrent, updateBooking } from '../booking';
import { submitToGoogleAnalytics } from '../google_analytics';

const upSell = createAction('checkout/upSell');
const reviewCart = createAction('checkout/reviewCart');
const checkoutBookingStarted = createAction('checkout/checkoutBookingStarted');
const checkoutBookingSucceeded = createAction(
  'checkout/checkoutBookingSucceeded'
);
const checkoutBookingFailure = createAction('checkout/checkoutBookingFailure');
const getBraintreeClientTokenStarted = createAction(
  'checkout/getBraintreeClientTokenStarted'
);
const getBraintreeClientTokenSucceeded = createAction(
  'checkout/getBraintreeClientTokenSucceeded'
);
const getBraintreeClientTokenFailure = createAction(
  'checkout/getBraintreeClientTokenFailure'
);
const storeBraintreeClientToken = createAction(
  'checkout/storeBraintreeClientToken'
);
const setBraintreeFieldsValid = createAction(
  'checkout/setBraintreeFieldsValid'
);
const setCheckoutRedirect = createAction('checkout/setCheckoutRedirect');
const setCheckoutDialog = createAction('checkout/setCheckoutDialog');
const setPaymentTypeId = createAction('checkout/setPaymentTypeId');

const setPaymentAmount = createAction('checkout/setPaymentAmount');

const getBraintreeClientToken = () => (dispatch) => {
  dispatch(getBraintreeClientTokenStarted());
  return EcomEngineAPI.checkout
    .client_token()
    .then((result) => {
      dispatch(getBraintreeClientTokenSucceeded());
      dispatch(storeBraintreeClientToken(result));
    })
    .catch((error) => {
      trackError(error);
      dispatch(getBraintreeClientTokenFailure(error));
    });
};

const retryTimeout = 1000;

const pollCheckout = function ({
  uuid,
  tryCount = 120,
  payment_type_id = null,
  keepBooking = false,
}) {
  return (dispatch, getState) =>
    EcomEngineAPI.checkout
      .checkout_status({
        checkout_id: uuid,
        keepBooking,
      })
      .then(async (response) => {
        switch (response.progress) {
          case 'processing':
            if (tryCount <= 0) {
              dispatch(
                setCheckoutDialog({
                  header: 'There was an issue paying for your booking',
                  message: `
                    Unfortunately there's been a payment timeout while paying for your booking.
                    Please call our Sales Centre on
                    <a href="tel:${phoneNumberLink}">${phoneNumber}</a>
                    and quote booking reference
                    ${getState().booking.current?.reference}
                    for further assistance.`,
                  state: CheckoutDialogStatesEnum.error,
                })
              );
              return;
            }
            tryCount--;
            return setTimeout(
              () =>
                pollCheckout({ uuid, tryCount, payment_type_id, keepBooking })(
                  dispatch,
                  getState
                ),
              retryTimeout
            );
          case 'completed':
            if (response.successful) {
              const bookingId = getState().booking.current.id;

              // Update booking info, passing booking id to fetchCurrent because when booking is paid, it will be
              // removed from the PHP session, check the actionStatus in CheckoutController.php
              await dispatch(fetchCurrent(bookingId));
              dispatch(submitToGoogleAnalytics({ payment_type_id }));
              dispatch(setCheckoutRedirect(true));
              dispatch(
                setCheckoutDialog({
                  header: '',
                  message: '',
                  state: CheckoutDialogStatesEnum.closed,
                })
              );
            } else {
              dispatch(
                setCheckoutDialog({
                  header: 'There was an issue paying for your booking',
                  message: `
                  <p>${response.error}</p>
                  <p>An issue has occurred while paying for your booking.
                  Please call our sales centre on
                  <a href="tel:${phoneNumberLink}">${phoneNumber}</a>
                  and quote booking reference
                  ${getState().booking.current?.reference}
                  for further assistance.</p>`,
                  state: CheckoutDialogStatesEnum.error,
                })
              );
            }
            break;
          default:
            setTimeout(
              () =>
                pollCheckout({ uuid, tryCount, payment_type_id, keepBooking })(
                  dispatch,
                  getState
                ),
              retryTimeout
            );
        }
      })
      .catch((error) => {
        trackError(error);
        dispatch(
          setCheckoutDialog({
            header: 'There was an issue while fetching your payment status',
            message: `
                  <p>${error}</p>
                  <p>An issue has occurred while paying for your booking.
                  Please call our Sales Centre on
                  <a href="tel:${phoneNumberLink}">${phoneNumber}</a>
                  and quote booking reference
                  ${getState().booking.current?.reference}
                  for further assistance.</p>`,
            state: CheckoutDialogStatesEnum.error,
          })
        );
      });
};

const checkoutBooking =
  ({
    payment_data_nonce,
    uid,
    payment_type_id,
    reference,
    paymentAmount,
    subscribe_to_email_list,
    payment_method_token,
    voucherPayments,
    device_data,
    three_d_secure_authentication_id,
    giftCards,
    recaptchaToken,
    turnstileToken,
  }) =>
  (dispatch, getState) => {
    dispatch(checkoutBookingStarted());

    const params = {
      payment_data_nonce,
      uid,
      payment_type_id,
      reference,
      subscribe_to_email_list,
      payBalance: true,
      payment_method_token,
      three_d_secure_authentication_id,
      device_data,
    };
    let keepBooking = false;

    if (turnstileToken) {
      params.turnstile_token = turnstileToken;
    }

    if (giftCards) {
      params.gift_cards = giftCards;
      params.recaptcha_token = recaptchaToken;
      keepBooking = true;
    }

    if (paymentAmount === 'deposit') {
      params.payBalance = false;
      params.amount_in_cents =
        getState().booking.current.deposit_outstanding_in_cents;
    }

    if (!isEmpty(voucherPayments)) {
      params.voucher_payments = voucherPayments;
    }

    return EcomEngineAPI.checkout
      .checkout_booking(params)
      .then((result) => {
        dispatch(checkoutBookingSucceeded());
        dispatch(
          pollCheckout({
            uuid: params.uid,
            payment_type_id: params.payment_type_id,
            keepBooking,
          })
        );
        return result;
      })
      .catch((error) => {
        trackError(error);
        dispatch(
          setCheckoutDialog({
            header: 'There was an issue while paying your booking',
            message: error.message,
            state: CheckoutDialogStatesEnum.error,
          })
        );
      });
  };

const setSavedCreditCards = createAction(
  'savedCreditCards/setSavedCreditCards'
);
const finaliseOnAccountPayment =
  ({ bookingId, onAccountPaymentType }) =>
  (dispatch) =>
    EcomEngineAPI.booking.touch(bookingId).then((currentBooking) => {
      dispatch(updateBooking(currentBooking));
      dispatch(
        submitToGoogleAnalytics({
          payment_type_id: onAccountPaymentType.id,
        })
      );
    });

const setVoucherCodes = createAction('voucherCodes/setVoucherCodes');
const fetchVoucherCodeStarted = createAction(
  'voucherCodes/fetchVoucherCodeStarted'
);
const fetchVoucherCodeSucceeded = createAction(
  'voucherCodes/fetchVoucherCodeSucceeded'
);
const fetchVoucherCodeFailed = createAction(
  'voucherCodes/fetchVoucherCodeFailed'
);
const removeVodeCode = createAction('voucherCodes/removeVodeCode');

const fetchVoucherCode = (voucherCode) => (dispatch) => {
  dispatch(fetchVoucherCodeStarted());
  return EcomEngineAPI.checkout
    .checkVoucherBalance(voucherCode)
    .then((response) => {
      dispatch(fetchVoucherCodeSucceeded());
      dispatch(setVoucherCodes({ voucherCode, balance: response.balance }));
    })
    .catch((error) => {
      trackError(error);
      dispatch(fetchVoucherCodeFailed(error.message));
    });
};

export {
  upSell,
  reviewCart,
  checkoutBookingStarted,
  checkoutBookingSucceeded,
  checkoutBookingFailure,
  checkoutBooking,
  getBraintreeClientTokenStarted,
  getBraintreeClientTokenSucceeded,
  getBraintreeClientTokenFailure,
  getBraintreeClientToken,
  storeBraintreeClientToken,
  setBraintreeFieldsValid,
  setCheckoutRedirect,
  pollCheckout,
  finaliseOnAccountPayment,
  setCheckoutDialog,
  setPaymentTypeId,
  setPaymentAmount,
  setSavedCreditCards,
  setVoucherCodes,
  fetchVoucherCodeStarted,
  fetchVoucherCodeSucceeded,
  fetchVoucherCodeFailed,
  fetchVoucherCode,
  removeVodeCode,
};
